import styled from '@emotion/styled';
import { History, Location } from 'history';
import React, { useEffect, useState } from 'react';
import queryString from 'query-string';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import {
  COLOR_MUTED_TEXT,
  MOBILE_BREAKPOINT,
  COLOR_MUTED_BACKGROUND,
} from '../../components/PaywallsShared/constants';
import { AnchorAPI } from '../../modules/AnchorAPI';
import { Metadata } from '../../types/Metadata';

// Maps to ?type= query param value
export enum ERROR_TYPE {
  DEFAULT_ERROR = 'default',
  SPOTIFY_AUTH_ERROR = 'spotify-auth-error',
}

export const ERROR_TITLES = {
  [ERROR_TYPE.DEFAULT_ERROR]: 'An error has occured',
  [ERROR_TYPE.SPOTIFY_AUTH_ERROR]: 'Unable to authenticate',
};

export const ERROR_MESSAGES = {
  [ERROR_TYPE.DEFAULT_ERROR]: 'Something went wrong.',
  [ERROR_TYPE.SPOTIFY_AUTH_ERROR]:
    "Something went wrong and we weren't able to authenticate your Spotify account.",
};

export const ERROR_ROUTE = '/404';

const isValidErrorType = (
  errorTypeParam: string | string[] | null | undefined
): errorTypeParam is ERROR_TYPE =>
  typeof errorTypeParam === 'string' &&
  Object.values(ERROR_TYPE).includes(errorTypeParam as ERROR_TYPE);

interface PaywallsSubscriptionErrorProps {
  history: History;
  location: Location;
  match: {
    params: {
      vanitySlug: string;
    };
  };
}

export const PaywallsSubscriptionError = ({
  history,
  location: { search },
  match: {
    params: { vanitySlug },
  },
}: PaywallsSubscriptionErrorProps) => {
  const [metadata, setMetadata] = useState<Metadata | null>(null);
  const { type } = queryString.parse(search);
  const forwardTo404 = () => history.push(ERROR_ROUTE);
  const errorType = isValidErrorType(type) ? type : ERROR_TYPE.DEFAULT_ERROR;

  if (!vanitySlug) {
    forwardTo404();
    return null;
  }

  useEffect(() => {
    const getData = async () => {
      try {
        const stationId = await AnchorAPI.getStationIdForVanityName(vanitySlug);
        const metaResponse = await AnchorAPI.getPodcastMetadata(stationId);
        // I think we can eventually remove this check and the metadata call, but this keeps the URL feature-flagged for now
        if (!metaResponse?.stationPaywall?.isSubscriptionEnabled) {
          return forwardTo404();
        }
        setMetadata(metaResponse);
      } catch (err) {
        forwardTo404();
      }
    };
    if (vanitySlug) {
      getData();
    }
  }, [vanitySlug]);

  if (!metadata) {
    return (
      <Container>
        <LoadingSpinner />
      </Container>
    );
  }

  return (
    <Container>
      <Header>{ERROR_TITLES[errorType]}</Header>
      <Content>
        {ERROR_MESSAGES[errorType]} Reach out to us at{' '}
        <a
          href="https://help.anchor.fm"
          target="_blank"
          rel="noopener noreferrer"
        >
          help.anchor.fm
        </a>{' '}
        for more information.
      </Content>
    </Container>
  );
};

const Container = styled.div`
  background: ${COLOR_MUTED_BACKGROUND};
  display: flex;
  align-items: center;
  text-align: center;
  padding: 70px;
  min-height: 400px;
  flex-direction: column;
`;

const Header = styled.h1`
  font-size: 2.4rem;
  font-weight: 800;
  display: block;
  margin: 0 0 20px 0;
`;

const Content = styled.div`
  font-size: 1.6rem;
  color: ${COLOR_MUTED_TEXT};
  max-width: ${MOBILE_BREAKPOINT}px;
  line-height: 1.5;

  a {
    color: inherit;
  }
`;
